<template>
  <div
    class="statement-detail"
    v-loading="homeLoading"
    element-loading-text="加载中......"
  >
    <div class="page-header">
      <div class="page-header__left">
        <h2 class="page-header__title">{{ pageTitle }}</h2>
        <el-tag
          v-if="Number(docData.statusCheck) === 1"
          size="small"
          type="danger"
          effect="dark"
          class="page-header__tag"
        >
          已审核
        </el-tag>
        <el-tag
          v-if="confirmStatusLabel"
          size="small"
          :type="confirmTagType"
          effect="plain"
          class="page-header__tag"
        >
          {{ confirmStatusLabel }}
        </el-tag>
      </div>
      <div class="page-header__actions">
        <el-button size="small" plain icon="el-icon-back" @click="backHome">
          返回
        </el-button>
        <el-button
          v-if="showConfirm"
          size="small"
          type="primary"
          :loading="confirmLoading"
          @click="handleConfirm"
        >
          确认
        </el-button>
        <el-button
          v-if="showObject"
          size="small"
          type="warning"
          @click="handleObject"
        >
          提出异议
        </el-button>
        <el-button
          v-if="showCancel"
          size="small"
          type="info"
          :loading="cancelLoading"
          @click="handleCancel"
        >
          撤销确认
        </el-button>
      </div>
    </div>

    <div class="section-title">丨基本信息</div>
    <el-descriptions
      class="info-desc"
      size="small"
      :column="5"
      border
      :label-style="labelStyle"
      :content-style="contentStyle"
    >
      <el-descriptions-item label="账单编号">
        {{ display(docData.purchasesStatementDocNumber) }}
      </el-descriptions-item>
      <el-descriptions-item label="全局搜索码">
        {{ display(docData.globalSearchNumber) }}
      </el-descriptions-item>
      <el-descriptions-item label="账单日期">
        {{ formatDate(docData.baseStatementDate) }}
      </el-descriptions-item>
      <el-descriptions-item label="预付款日期">
        {{ formatDate(docData.baseExpectedPaymentDate) }}
      </el-descriptions-item>
      <el-descriptions-item label="币种">
        {{ formatCurrency(docData.baseCurrency) }}
      </el-descriptions-item>

      <el-descriptions-item label="业务类型">
        {{ modelTypeLabel }}
      </el-descriptions-item>
      <el-descriptions-item label="委外商编号">
        {{ display(docData.baseSupplierNumber) }}
      </el-descriptions-item>
      <el-descriptions-item label="委外商名称">
        {{ display(docData.baseSupplierName) }}
      </el-descriptions-item>
      <el-descriptions-item label="付款方式">
        {{ display(docData.valuePaymentMode) }}
      </el-descriptions-item>
      <el-descriptions-item label="应付金额(折前)">
        {{ formatMoney(docData.baseShouldPaymentAmount) }}
      </el-descriptions-item>

      <el-descriptions-item label="应付金额(折后)">
        {{ formatMoney(docData.baseShouldPaymentAmountDiscounted) }}
      </el-descriptions-item>
      <el-descriptions-item label="折扣率(%)">
        {{ formatDiscount(docData.baseDiscount) }}
      </el-descriptions-item>
      <el-descriptions-item label="未付金额">
        {{ formatMoney(docData.valueUnpaidAmount) }}
      </el-descriptions-item>
      <el-descriptions-item label="已付金额">
        {{ formatMoney(docData.valuePaidAmount) }}
      </el-descriptions-item>
      <el-descriptions-item label="应开发票(折前)">
        {{ formatMoney(docData.baseShouldInvoiceAmount) }}
      </el-descriptions-item>

      <el-descriptions-item label="应开发票(折后)">
        {{ formatMoney(docData.baseShouldInvoiceAmountDiscounted) }}
      </el-descriptions-item>
      <el-descriptions-item label="未开发票">
        {{ formatMoney(docData.valueUninvoicedAmount) }}
      </el-descriptions-item>
      <el-descriptions-item label="已开发票">
        {{ formatMoney(docData.valueInvoicedAmount) }}
      </el-descriptions-item>
      <el-descriptions-item label="请款金额">
        {{ formatMoney(docData.valueRequestFundsAmount) }}
      </el-descriptions-item>
      <el-descriptions-item label="确认状态">
        {{ confirmStatusLabel || '--' }}
      </el-descriptions-item>

      <el-descriptions-item
        v-if="docData.baseSupplierConfirmUserName"
        label="确认/异议人"
      >
        {{ display(docData.baseSupplierConfirmUserName) }}
      </el-descriptions-item>
      <el-descriptions-item
        v-if="docData.baseSupplierConfirmTime"
        label="确认/异议时间"
      >
        {{ display(docData.baseSupplierConfirmTime) }}
      </el-descriptions-item>
      <el-descriptions-item
        v-if="docData.valueSupplierObjectionRemark"
        :span="3"
        label="异议说明"
      >
        {{ display(docData.valueSupplierObjectionRemark) }}
      </el-descriptions-item>
      <el-descriptions-item
        v-if="objectionFileText"
        :span="2"
        label="异议附件"
      >
        <myFileCount
          size="mini"
          :fileText="objectionFileText"
          :type="3"
          :status="1"
          dialogTitle="异议附件"
        />
      </el-descriptions-item>
    </el-descriptions>

    <div class="section-head">
      <div class="section-title">丨{{ detailSectionTitle }}</div>
      <dialogDragSort
        v-if="tableSettingReady"
        :flag="tableSettingReady"
        :titleList="titleList"
        @onEnd="onTableSettingChange"
        @isShowClick="onTableSettingChange"
        @widthClick="onTableSettingChange"
        @resetClick="resetTableSetting"
      />
    </div>

    <myHtml
      style="margin-top: 10px"
      :id="'statement_detail_table'"
      :columnType="'columnIndex'"
      :list="detailList"
      :titleList="titleList"
      :tableBtnList="[]"
      :height="detailTableHeight"
      @resizableChange="resizableChange"
      ref="detailTable"
    />

    <div class="summary-bar">
      <div class="summary-bar__item">
        <label>对账数量合计</label>
        <strong>{{ detailQtyTotal }}</strong>
      </div>
      <div class="summary-bar__item">
        <label>总金额</label>
        <strong>{{ formatMoney(docData.baseShouldPaymentAmount) }}</strong>
      </div>
      <div class="summary-bar__item">
        <label>折后金额</label>
        <strong>{{
          formatMoney(docData.baseShouldPaymentAmountDiscounted)
        }}</strong>
      </div>
      <div class="summary-bar__item">
        <label>折扣率(%)</label>
        <strong>{{ formatDiscount(docData.baseDiscount) }}</strong>
      </div>
      <div class="summary-bar__item summary-bar__item--remark">
        <label>备注</label>
        <span>{{ display(docData.remark) }}</span>
      </div>
    </div>

    <statement-object-dialog
      :visible.sync="objectDialogVisible"
      :row="docData"
      @success="getList"
    />
  </div>
</template>

<script>
import {
  selcetSetting,
  updateSetting,
  customMenu,
} from '@/utils/myUtils'
import {
  pmgtPurchasesStatementDoc_getCooperativeSupplierPurchaseStatementDocView,
  pmgtPurchasesStatementDoc_confirmStatement,
  pmgtPurchasesStatementDoc_cancelConfirm,
} from '@/api/myController/outsourcing'
import {
  STATEMENT_MODEL_TYPE,
  STATEMENT_CONFIRM_STATUS,
  getStatementModelTypeLabel,
  getStatementConfirmStatus,
  getStatementConfirmStatusLabel,
  canConfirmStatement,
  canObjectStatement,
  canCancelStatementConfirm,
  parseObjectionFileList,
  normalizeStatementDetailRow,
  formatStatementMoney,
  formatStatementDate,
  formatStatementDateTime,
  sumStatementDetailAmount,
} from '@/utils/outsourcingStatement'
import StatementObjectDialog from './statement_object_dialog.vue'

export default {
  name: 'outsourcing_statement_details',
  components: {
    StatementObjectDialog,
  },
  props: {
    routeType: {
      type: Array,
      default: undefined,
    },
    routeQuery: {
      default: () => ({}),
    },
  },
  data() {
    return {
      homeLoading: false,
      confirmLoading: false,
      cancelLoading: false,
      objectDialogVisible: false,
      docData: {},
      pmgtPurchasesStatementDoc_getCooperativeSupplierPurchaseStatementDocView,
      detailList: [],
      titleList: [],
      defaultTitleList: [],
      tableSettingKey: 'outsourcingProduct_statement_details_detail',
      tableSettingReady: false,
      detailTableHeight: 360,
      labelStyle: {
        minWidth: '110px',
        height: '40px',
        lineHeight: '28px',
        textAlign: 'center',
        background: '#fafafa',
        color: '#606266',
      },
      contentStyle: {
        width: '140px',
      },
    }
  },
  computed: {
    pageTitle() {
      const name = this.docData.docName || '委外账单'
      const number = this.docData.purchasesStatementDocNumber
      return number ? `${name}-${number}` : name
    },
    modelTypeLabel() {
      return getStatementModelTypeLabel(this.docData.modelType)
    },
    isProcess() {
      return Number(this.docData.modelType) === STATEMENT_MODEL_TYPE.PROCESS
    },
    detailSectionTitle() {
      return this.isProcess ? '工序/产品信息' : '产品信息'
    },
    confirmStatus() {
      return getStatementConfirmStatus(this.docData)
    },
    confirmStatusLabel() {
      return getStatementConfirmStatusLabel(this.confirmStatus)
    },
    confirmTagType() {
      if (this.confirmStatus === STATEMENT_CONFIRM_STATUS.CONFIRMED) {
        return 'success'
      }
      if (this.confirmStatus === STATEMENT_CONFIRM_STATUS.OBJECTED) {
        return 'danger'
      }
      return 'warning'
    },
    showConfirm() {
      return canConfirmStatement(this.docData)
    },
    showObject() {
      return canObjectStatement(this.docData)
    },
    showCancel() {
      return canCancelStatementConfirm(this.docData)
    },
    objectionFiles() {
      return parseObjectionFileList(this.docData.valueSupplierObjectionFileList)
    },
    objectionFileText() {
      if (!this.objectionFiles.length) return ''
      const raw = this.docData.valueSupplierObjectionFileList
      if (typeof raw === 'string' && raw.trim()) return raw
      return JSON.stringify(this.objectionFiles)
    },
    detailQtyTotal() {
      return sumStatementDetailAmount(this.detailList)
    },
  },
  created() {
    this.defaultTitleList = JSON.parse(
      JSON.stringify(this.buildDefaultTitleList())
    )
    this.titleList = JSON.parse(JSON.stringify(this.defaultTitleList))
    this.detailTableHeight = Math.max(
      280,
      (this.$root.tableHeight || 600) - 320
    )
    this.initTableSetting().then(() => {
      this.tableSettingReady = true
      this.getList()
    })
  },
  methods: {
    display(value) {
      if (value === undefined || value === null || value === '') return '--'
      return value
    },
    formatDate: formatStatementDate,
    formatDateTime: formatStatementDateTime,
    formatCurrency(value) {
      if (!value) return '人民币'
      if (value === 'CNY' || value === 'RMB' || value === '人民币') {
        return '人民币'
      }
      return value
    },
    formatMoney: formatStatementMoney,
    formatDiscount(value) {
      if (value === undefined || value === null || value === '') return '--'
      const num = Number(value)
      if (Number.isNaN(num)) return value
      if (num > 0 && num <= 1.5) {
        return Math.round(num * 10000) / 100
      }
      return num
    },
    buildDefaultTitleList() {
      return [
        {
          id: 1,
          name: '生产单号',
          value: '_produceDocNumber',
          fixedState: false,
          isShow: true,
          dataType: 'text',
          canEdit: false,
          width: 120,
        },
        {
          id: 2,
          name: '单据编号',
          value: '_docNumber',
          fixedState: false,
          isShow: true,
          dataType: 'text',
          canEdit: false,
          width: 120,
        },
        {
          id: 3,
          name: '收货单号',
          value: '_receiptNumber',
          fixedState: false,
          isShow: true,
          dataType: 'text',
          canEdit: false,
          width: 120,
        },
        {
          id: 4,
          name: '全局搜索码',
          value: '_globalSearchNumber',
          fixedState: false,
          isShow: true,
          dataType: 'text',
          canEdit: false,
          width: 130,
        },
        {
          id: 5,
          name: '入库时间',
          value: '_entranceTime',
          fixedState: false,
          isShow: true,
          dataType: 'text',
          canEdit: false,
          width: 150,
        },
        {
          id: 6,
          name: '工序编号',
          value: '_processNumber',
          fixedState: false,
          isShow: false,
          dataType: 'text',
          canEdit: false,
          width: 110,
          processOnly: true,
        },
        {
          id: 7,
          name: '工序名称',
          value: '_processName',
          fixedState: false,
          isShow: false,
          dataType: 'text',
          canEdit: false,
          width: 120,
          processOnly: true,
        },
        {
          id: 8,
          name: '产品编号',
          value: '_materialNumber',
          fixedState: false,
          isShow: true,
          dataType: 'text',
          canEdit: false,
          color: '#409eff',
          width: 120,
        },
        {
          id: 9,
          name: '产品名称',
          value: '_materialName',
          fixedState: false,
          isShow: true,
          dataType: 'text',
          canEdit: false,
          width: 140,
        },
        {
          id: 10,
          name: '产品规格',
          value: '_specText',
          fixedState: false,
          isShow: true,
          dataType: 'text',
          canEdit: false,
          width: 160,
        },
        {
          id: 11,
          name: '单位',
          value: '_unitName',
          fixedState: false,
          isShow: true,
          dataType: 'text',
          canEdit: false,
          width: 70,
        },
        {
          id: 12,
          name: '本次对账数量',
          value: 'baseAmount',
          fixedState: false,
          isShow: true,
          dataType: 'numberAccuracy',
          canEdit: false,
          width: 110,
        },
        {
          id: 13,
          name: '含税单价',
          value: 'baseMaterialUnitprice',
          fixedState: false,
          isShow: true,
          dataType: 'priceAccuracy',
          canEdit: false,
          width: 100,
        },
        {
          id: 14,
          name: '账单小计(含税)',
          value: 'baseMaterialTotalprice',
          fixedState: false,
          isShow: true,
          dataType: 'priceAccuracy',
          canEdit: false,
          width: 120,
        },
      ]
    },
    async initTableSetting() {
      const saved = await selcetSetting(this.tableSettingKey)
      if (saved == null) {
        this.titleList = JSON.parse(JSON.stringify(this.defaultTitleList))
        updateSetting(this, this.tableSettingKey, JSON.stringify(this.titleList))
      } else {
        this.titleList = customMenu(saved, this.defaultTitleList, [])
      }
    },
    onTableSettingChange(titleList) {
      this.titleList = titleList
      updateSetting(this, this.tableSettingKey, JSON.stringify(titleList))
    },
    resetTableSetting() {
      this.titleList = JSON.parse(JSON.stringify(this.defaultTitleList))
      this.syncProcessColumnVisibility()
      updateSetting(this, this.tableSettingKey, JSON.stringify(this.titleList))
    },
    resizableChange(val) {
      this.titleList.forEach((item) => {
        if (item.value === val.field && item.name === val.title) {
          item.width = val.renderWidth
        }
      })
      updateSetting(this, this.tableSettingKey, JSON.stringify(this.titleList))
    },
    syncProcessColumnVisibility() {
      const showProcess = this.isProcess
      this.titleList.forEach((item) => {
        if (item.processOnly) {
          item.isShow = showProcess
        }
      })
    },
    getList() {
      const id =
        this.routeQuery.purchasesStatementDocId ||
        this.routeQuery.id
      if (!id) {
        this.$message.error('缺少对账单标识')
        return
      }
      this.homeLoading = true
      pmgtPurchasesStatementDoc_getCooperativeSupplierPurchaseStatementDocView({
        purchasesStatementDocId: id,
      })
        .then((res) => {
          const data = (res.data && res.data.data) || {}
          this.docData = data
          const list =
            data.purchasesStatementDocDetailList ||
            data.pmgtPurchasesStatementDocDetailList ||
            []
          this.detailList = (list || []).map((item) =>
            normalizeStatementDetailRow(item)
          )
          this.syncProcessColumnVisibility()
        })
        .catch(() => {
          this.docData = {}
          this.detailList = []
        })
        .finally(() => {
          this.homeLoading = false
        })
    },
    backHome() {
      this.$emit('backHome')
    },
    handleConfirm() {
      if (!canConfirmStatement(this.docData)) {
        this.$message.warning('当前状态不可确认')
        return
      }
      this.$confirm('确认该对账单无误？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(() => {
          this.confirmLoading = true
          return pmgtPurchasesStatementDoc_confirmStatement({
            dto: {
              purchasesStatementDocId: this.docData.purchasesStatementDocId,
            }
          })
        })
        .then((res) => {
          if (res.data && res.data.success === false) {
            this.$message.error(res.data.errorMessage || '确认失败')
            return
          }
          this.$message.success('确认成功')
          this.getList()
        })
        .catch(() => {})
        .finally(() => {
          this.confirmLoading = false
        })
    },
    handleObject() {
      if (!canObjectStatement(this.docData)) {
        this.$message.warning('当前状态不可提出异议')
        return
      }
      this.objectDialogVisible = true
    },
    handleCancel() {
      if (!canCancelStatementConfirm(this.docData)) {
        this.$message.warning('当前状态不可撤销确认')
        return
      }
      this.$confirm('撤销后将回到待确认，是否继续？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(() => {
          this.cancelLoading = true
          return pmgtPurchasesStatementDoc_cancelConfirm({
            dto: {
              purchasesStatementDocId: this.docData.purchasesStatementDocId,
            }
          })
        })
        .then((res) => {
          if (res.data && res.data.success === false) {
            this.$message.error(res.data.errorMessage || '撤销失败')
            return
          }
          this.$message.success('已撤销')
          this.getList()
        })
        .catch(() => {})
        .finally(() => {
          this.cancelLoading = false
        })
    },
  },
}
</script>

<style scoped lang="scss">
.statement-detail {
  min-height: calc(100vh - 160px);
  padding: 4px 8px 24px;
  background: #fff;
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
}

.page-header__left {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.page-header__title {
  margin: 0;
  color: #1476c3;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.3;
}

.page-header__tag {
  margin-left: 2px;
}

.page-header__actions {
  display: flex;
  flex-wrap: wrap;
}

.section-title {
  margin: 12px 0 10px;
  color: #1476c3;
  font-size: 16px;
  font-weight: 800;
}

.section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 18px;
}

.section-head .section-title {
  margin: 0;
}

.info-desc {
  margin-bottom: 8px;
}

.summary-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 24px;
  padding: 12px 16px;
  background: #f5f7fa;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}

.summary-bar__item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #606266;
  font-size: 13px;

  label {
    color: #909399;
  }

  strong {
    color: #303133;
    font-variant-numeric: tabular-nums;
  }
}

.summary-bar__item--remark {
  flex: 1;
  min-width: 180px;
}
</style>
